import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

// Create axios instance
const api = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  verifyOtp: (data) => api.post('/auth/verify-otp', data),
  resendOtp: (data) => api.post('/auth/resend-otp', data),
  emergencyLock: (token) => api.post('/auth/emergency-lock', { token }),
  logout: () => api.post('/auth/logout'),
  dismissOnboarding: () => api.post('/users/me/onboarding-dismiss'),
  getMe: () => api.get('/auth/me'),
  resendVerification: () => api.post('/auth/resend-verification'),
  resendVerificationUnauth: (email, password) => api.post('/auth/resend-verification-unauth', { email, password }),
  changePassword: (currentPassword, newPassword) => api.post('/auth/change-password', {
    current_password: currentPassword,
    new_password: newPassword
  }),
};

// Facilitator API
export const facilitatorAPI = {
  list: (params) => api.get('/facilitators', { params }),
  get: (id) => api.get(`/facilitators/${id}`),
  create: (data) => api.post('/facilitators', data),
  update: (id, data) => api.put(`/facilitators/${id}`, data),
  getMyProfile: () => api.get('/facilitators/me/profile'),
  getMyDocuments: () => api.get('/facilitators/me/documents'),
  deleteDocument: (id) => api.delete(`/facilitators/me/documents/${id}`),
};

// Camp API
export const campAPI = {
  list: (params) => api.get('/camps', { params }),
  get: (id) => api.get(`/camps/${id}`),
  create: (data) => api.post('/camps', data),
  update: (id, data) => api.put(`/camps/${id}`, data),
  getMyProfile: () => api.get('/camps/me/profile'),
};

// Review API
export const reviewAPI = {
  list: (params) => api.get('/reviews', { params }),
  create: (data) => api.post('/reviews', data),
  dispute: (id, data) => api.post(`/reviews/${id}/dispute`, data),
};

// Admin API
export const adminAPI = {
  getFacilitators: () => api.get('/admin/facilitators'),
  getDocument: (filename) => api.get(`/files/documents/${filename}`, { responseType: 'blob' }),
  reviewDocument: (facilitatorId, documentId, status, reason) =>
    api.put(`/admin/facilitators/${facilitatorId}/documents/${documentId}/review`, { status, reason }),
  updateReference: (facilitatorId, referenceId, contacted, notes) =>
    api.put(`/admin/facilitators/${facilitatorId}/references/${referenceId}`, { contacted, notes }),
  getPendingDocsCount: () => api.get('/admin/documents/pending-count'),
  getPendingDocsQueue: () => api.get('/admin/documents/pending'),
  createFacilitator: (data) => api.post('/admin/facilitators', data),
  updateFacilitator: (id, data) => api.put(`/admin/facilitators/${id}`, data),
  deleteFacilitator: (id) => api.delete(`/admin/facilitators/${id}`),
  getCamps: () => api.get('/admin/camps'),
  createCamp: (data) => api.post('/admin/camps', data),
  verifyCamp: (id, data) => api.put(`/admin/camps/${id}/verify`, data),
  deleteCamp: (id) => api.delete(`/admin/camps/${id}`),
  getReviews: (params = {}) => api.get('/admin/reviews', { params }),
  approveReview: (id, approve) => api.put(`/admin/reviews/${id}/approve?approve=${approve}`),
  deleteReview: (id) => api.delete(`/admin/reviews/${id}`),
  dismissDispute: (id) => api.put(`/admin/reviews/${id}/dismiss-dispute`),
  getUsers: () => api.get('/admin/users'),
  suspendUser: (id, suspended) => api.put(`/admin/users/${id}/suspend?suspended=${suspended}`),
  deleteUser: (id) => api.delete(`/admin/users/${id}`),
  getIncidents: () => api.get('/admin/incidents'),
  createIncident: (data) => api.post('/admin/incidents', data),
  updateIncident: (id, data) => api.put(`/admin/incidents/${id}`, data),
  deleteIncident: (id) => api.delete(`/admin/incidents/${id}`),
  getAuditLogs: (params = {}) => api.get('/admin/audit-logs', { params }),
  getTrustedDevices: () => api.get('/admin/trusted-devices'),
  renameTrustedDevice: (id, label) => api.put(`/admin/trusted-devices/${id}`, { label }),
  revokeTrustedDevice: (id) => api.delete(`/admin/trusted-devices/${id}`),
  revokeAllTrustedDevices: () => api.delete('/admin/trusted-devices'),
  getLoginHistory: () => api.get('/admin/login-history'),
  exportAuditLogs: () => api.get('/admin/audit-logs/export', { responseType: 'blob' }),
  exportIncidents: () => api.get('/admin/incidents/export', { responseType: 'blob' }),
  downloadCompliancePack: () => api.get('/admin/compliance-pack', { responseType: 'blob' }),
  importFacilitators: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/admin/import/facilitators', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  importCamps: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/admin/import/camps', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
};

// Public API
export const publicAPI = {
  getStats: () => api.get('/public/stats'),
  getProvinces: () => api.get('/public/provinces'),
  getActivities: () => api.get('/public/activities'),
  getCampTypes: () => api.get('/public/camp-types'),
  getAgeGroups: () => api.get('/public/age-groups'),
};

// Seed demo data
export const seedDemoData = () => api.post('/seed-demo-data');

// File Upload API
export const uploadAPI = {
  profilePhoto: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/upload/profile-photo', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  document: (file, documentType, documentLabel, notifyResubmit = false, replacesDocumentId = null) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('document_type', documentType);
    formData.append('document_label', documentLabel);
    if (notifyResubmit) formData.append('notify_resubmit', 'true');
    if (replacesDocumentId) formData.append('replaces_document_id', replacesDocumentId);
    return api.post('/upload/document', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
};

// Password Reset API
export const passwordAPI = {
  requestReset: (email) => api.post('/auth/password-reset-request', { email }),
  confirmReset: (token, newPassword) => api.post('/auth/password-reset-confirm', { token, new_password: newPassword }),
};

// Profile Claim API
export const claimAPI = {
  facilitator: (id) => api.post(`/facilitators/${id}/claim`),
  camp: (id) => api.post(`/camps/${id}/claim`),
};

export default api;
